import { useCallback, useEffect, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  Building2,
  CheckCircle2,
  Clock3,
  DollarSign,
  Edit,
  Loader2,
  Percent,
  Plus,
  ReceiptText,
  School,
  Send,
  Trash2,
  Users,
  WalletCards,
} from 'lucide-react';


const FORM_INICIAL = {
  nome: '',
  email: '',
  percentual: '',
  turma_ids: [],
  password: '',
};


function formatPercentual(valor) {
  return `${Number(valor || 0).toLocaleString('pt-BR', {
    maximumFractionDigits: 2,
  })}%`;
}


function formatMoeda(valor) {
  return Number(valor || 0).toLocaleString(
    'pt-BR',
    {
      style: 'currency',
      currency: 'BRL',
    }
  );
}


function competenciaAtual() {
  const agora = new Date();

  return [
    agora.getFullYear(),
    String(agora.getMonth() + 1).padStart(2, '0'),
  ].join('-');
}


function statusRepasse(status) {
  const mapa = {
    sem_valor: {
      label: 'Sem valor',
      classe: 'bg-zinc-800 text-zinc-400',
    },
    a_pagar: {
      label: 'A pagar',
      classe: 'bg-red-500/10 text-red-400',
    },
    parcialmente_pago: {
      label: 'Parcialmente pago',
      classe: 'bg-amber-500/10 text-amber-400',
    },
    aguardando_confirmacao: {
      label: 'Aguardando confirmação',
      classe: 'bg-blue-500/10 text-blue-400',
    },
    quitado: {
      label: 'Quitado',
      classe: 'bg-emerald-500/10 text-emerald-400',
    },
  };

  return mapa[status] || mapa.sem_valor;
}


export default function ParceriasPage() {

  const [parcerias, setParcerias] = useState([]);
  const [turmas, setTurmas] = useState([]);

  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState(FORM_INICIAL);

  const [competencia, setCompetencia] = useState(
    competenciaAtual()
  );

  const [financeiro, setFinanceiro] = useState({
    competencia: competenciaAtual(),
    quantidade_parcerias: 0,
    totais: {},
    parcerias: [],
  });

  const [
    loadingFinanceiro,
    setLoadingFinanceiro
  ] = useState(true);

  const [
    repasseDialogOpen,
    setRepasseDialogOpen
  ] = useState(false);

  const [
    parceriaRepasse,
    setParceriaRepasse
  ] = useState(null);

  const [
    salvandoRepasse,
    setSalvandoRepasse
  ] = useState(false);

  const [repasseForm, setRepasseForm] = useState({
    valor: '',
    data_deposito: new Date()
      .toISOString()
      .slice(0, 10),
    observacao: '',
    gerar_saldo_positivo: false,
    comprovante: null,
  });

  const [repassesHistorico, setRepassesHistorico] =
    useState([]);

  const [
    loadingRepassesHistorico,
    setLoadingRepassesHistorico
  ] = useState(false);


  const carregar = useCallback(async () => {

    setLoading(true);

    try {

      const [resParcerias, resTurmas] = await Promise.all([
        api.get('/gestor/parcerias'),
        api.get('/gestor/turmas'),
      ]);

      setParcerias(
        Array.isArray(resParcerias.data)
          ? resParcerias.data
          : []
      );

      setTurmas(
        Array.isArray(resTurmas.data)
          ? resTurmas.data.filter(t => t.ativo !== false)
          : []
      );

    } catch (err) {

      toast.error(
        formatApiError(err)
        || 'Erro ao carregar parcerias'
      );

    } finally {

      setLoading(false);

    }

  }, []);


  useEffect(() => {
    carregar();
  }, [carregar]);


  const carregarFinanceiro = useCallback(async () => {

    setLoadingFinanceiro(true);

    try {
      const resposta = await api.get(
        '/gestor/parcerias/financeiro/resumo',
        {
          params: { competencia },
        }
      );

      setFinanceiro(
        resposta.data || {
          competencia,
          quantidade_parcerias: 0,
          totais: {},
          parcerias: [],
        }
      );

    } catch (err) {
      toast.error(
        formatApiError(err)
        || 'Erro ao carregar financeiro das parcerias'
      );

    } finally {
      setLoadingFinanceiro(false);
    }

  }, [competencia]);


  useEffect(() => {
    carregarFinanceiro();
  }, [carregarFinanceiro]);


  const obterFinanceiroParceria = (parceriaId) => {
    const itens = Array.isArray(financeiro?.parcerias)
      ? financeiro.parcerias
      : [];

    return itens.find(
      item => item.id === parceriaId
    ) || null;
  };


  const carregarHistoricoRepasses = async (
    parceriaId
  ) => {

    if (!parceriaId) {
      setRepassesHistorico([]);
      return;
    }

    setLoadingRepassesHistorico(true);

    try {

      const resposta = await api.get(
        `/gestor/parcerias/${parceriaId}/repasses`,
        {
          params: { competencia },
        }
      );

      const itens = Array.isArray(
        resposta.data?.repasses
      )
        ? resposta.data.repasses
        : [];

      setRepassesHistorico(itens);

    } catch (err) {

      setRepassesHistorico([]);

      toast.error(
        formatApiError(err)
        || 'Não foi possível carregar o histórico de repasses'
      );

    } finally {

      setLoadingRepassesHistorico(false);

    }
  };


  const abrirRepasse = async (parceria) => {
    const dados = obterFinanceiroParceria(
      parceria.id
    );

    if (!dados) {
      toast.error(
        'Dados financeiros da parceria não disponíveis'
      );
      return;
    }

    const saldo = Number(
      dados.saldo_a_repassar || 0
    );

    setParceriaRepasse({
      ...parceria,
      financeiro: dados,
    });

    setRepasseForm({
      valor: saldo > 0
        ? String(saldo.toFixed(2))
        : '',
      data_deposito: new Date()
        .toISOString()
        .slice(0, 10),
      observacao: '',
      gerar_saldo_positivo: false,
      comprovante: null,
    });

    setRepassesHistorico([]);
    setRepasseDialogOpen(true);

    await carregarHistoricoRepasses(
      parceria.id
    );
  };


  const registrarRepasse = async (event) => {
    event.preventDefault();

    if (!parceriaRepasse?.id) {
      return;
    }

    const valor = Number(
      String(repasseForm.valor)
        .replace(',', '.')
    );

    if (
      !Number.isFinite(valor)
      || valor <= 0
    ) {
      toast.error(
        'Informe um valor de repasse válido'
      );
      return;
    }

    setSalvandoRepasse(true);

    try {
      const resposta = await api.post(
        `/gestor/parcerias/${parceriaRepasse.id}/repasses`,
        {
          competencia,
          valor,
          data_deposito:
            repasseForm.data_deposito,
          observacao:
            repasseForm.observacao.trim(),
          gerar_saldo_positivo:
            Boolean(repasseForm.gerar_saldo_positivo),
        }
      );

      const repasseId = resposta.data
        ?.repasse?.id;

      if (repasseForm.comprovante && repasseId) {
        const formData = new FormData();

        formData.append(
          'arquivo',
          repasseForm.comprovante
        );

        try {
          await api.post(
            `/gestor/parcerias/${parceriaRepasse.id}/repasses/${repasseId}/comprovante`,
            formData
          );
        } catch (uploadErr) {
          toast.warning(
            'Repasse registrado, mas o comprovante não pôde ser enviado'
          );
        }
      }

      toast.success(
        'Repasse registrado e aguardando confirmação'
      );

      setRepasseDialogOpen(false);
      setParceriaRepasse(null);

      await carregarFinanceiro();

    } catch (err) {
      toast.error(
        formatApiError(err)
        || 'Não foi possível registrar o repasse'
      );

    } finally {
      setSalvandoRepasse(false);
    }
  };


  const abrirNova = () => {

    setEditing(null);
    setForm(FORM_INICIAL);
    setDialogOpen(true);

  };


  const abrirEdicao = (parceria) => {

    setEditing(parceria);

    setForm({
      nome: parceria.nome || '',
      email: parceria.email || '',
      percentual:
        parceria.percentual === undefined
          ? ''
          : String(parceria.percentual),
      turma_ids:
        Array.isArray(parceria.turma_ids)
          ? parceria.turma_ids
          : (
              Array.isArray(parceria.turmas)
                ? parceria.turmas
                    .map(t => t.id)
                    .filter(Boolean)
                : []
            ),
      password: '',
    });

    setDialogOpen(true);

  };


  const alternarTurma = (turmaId) => {

    setForm(prev => {

      const selecionada =
        prev.turma_ids.includes(turmaId);

      return {
        ...prev,
        turma_ids: selecionada
          ? prev.turma_ids.filter(id => id !== turmaId)
          : [...prev.turma_ids, turmaId],
      };

    });

  };


  const salvar = async (event) => {

    event.preventDefault();

    const nome = form.nome.trim();
    const email = form.email.trim().toLowerCase();
    const percentual = Number(form.percentual);

    if (!nome) {
      toast.error('Informe o nome da parceria');
      return;
    }

    if (!email) {
      toast.error('Informe o e-mail do parceiro');
      return;
    }

    if (
      !Number.isFinite(percentual)
      || percentual < 0
      || percentual > 100
    ) {
      toast.error(
        'O percentual deve estar entre 0 e 100'
      );
      return;
    }

    if (!form.turma_ids.length) {
      toast.error(
        'Selecione pelo menos uma turma'
      );
      return;
    }

    if (
      !editing
      && form.password
      && form.password.length < 6
    ) {
      toast.error(
        'A senha deve possuir pelo menos 6 caracteres'
      );
      return;
    }

    const payload = {
      nome,
      email,
      percentual,
      turma_ids: form.turma_ids,
    };

    if (form.password) {
      payload.password = form.password;
    }

    setSalvando(true);

    try {

      if (editing) {

        await api.put(
          `/gestor/parcerias/${editing.id}`,
          payload
        );

        toast.success('Parceria atualizada');

      } else {

        await api.post(
          '/gestor/parcerias',
          payload
        );

        toast.success('Parceria criada');

      }

      setDialogOpen(false);
      setEditing(null);
      setForm(FORM_INICIAL);

      await carregar();

    } catch (err) {

      toast.error(
        formatApiError(err)
        || 'Não foi possível salvar a parceria'
      );

    } finally {

      setSalvando(false);

    }

  };


  const desativar = async (parceria) => {

    const confirmou = window.confirm(
      `Desativar a parceria "${parceria.nome}"?\n\n`
      + 'O acesso do parceiro também será desativado.'
    );

    if (!confirmou) return;

    try {

      await api.delete(
        `/gestor/parcerias/${parceria.id}`
      );

      toast.success('Parceria desativada');

      await carregar();

    } catch (err) {

      toast.error(
        formatApiError(err)
        || 'Não foi possível desativar a parceria'
      );

    }

  };


  return (

    <div
      className="space-y-6 animate-fadeIn"
      data-testid="parcerias-page"
    >

      <div className="
        flex flex-col gap-4
        sm:flex-row sm:items-center sm:justify-between
      ">

        <div>

          <h1 className="
            text-3xl font-black
            font-['Barlow_Condensed']
            tracking-tight uppercase text-white
          ">
            PARCERIAS
          </h1>

          <p className="text-sm text-zinc-500 mt-1">
            Gerencie parceiros, percentuais e turmas vinculadas.
          </p>

        </div>

        <Button
          onClick={abrirNova}
          className="
            bg-[var(--cor-primaria,#007AFF)]
            hover:opacity-90
          "
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova parceria
        </Button>

      </div>


      <Card className="
        bg-[#141414]
        border-[#27272A]
      ">
        <CardContent className="p-5">

          <div className="
            flex flex-col gap-4
            lg:flex-row
            lg:items-center
            lg:justify-between
          ">

            <div>
              <div className="
                flex items-center gap-2
              ">
                <WalletCards className="
                  w-5 h-5
                  text-[var(--cor-primaria,#007AFF)]
                " />

                <h2 className="
                  text-xl font-black
                  font-['Barlow_Condensed']
                  uppercase text-white
                ">
                  Financeiro das parcerias
                </h2>
              </div>

              <p className="
                text-sm text-zinc-500 mt-1
              ">
                Acompanhe recebimentos e repasses
                das parcerias.
              </p>
            </div>

            <div>
              <Label className="
                text-xs text-zinc-500
              ">
                Competência
              </Label>

              <Input
                type="month"
                value={competencia}
                onChange={e =>
                  setCompetencia(e.target.value)
                }
                className="
                  mt-1 w-[170px]
                  bg-[#0A0A0A]
                  border-[#27272A]
                  text-white
                "
              />
            </div>

          </div>


          {loadingFinanceiro ? (

            <div className="
              grid grid-cols-2
              xl:grid-cols-4
              gap-3 mt-5
            ">
              {[1, 2, 3, 4].map(item => (
                <div
                  key={item}
                  className="
                    h-24 rounded-xl
                    bg-[#0A0A0A]
                    border border-[#27272A]
                    animate-pulse
                  "
                />
              ))}
            </div>

          ) : (

            <div className="
              grid grid-cols-2
              xl:grid-cols-4
              gap-3 mt-5
            ">

              <div className="
                rounded-xl bg-[#0A0A0A]
                border border-[#27272A]
                p-4
              ">
                <div className="
                  flex items-center gap-2
                  text-zinc-500
                ">
                  <DollarSign className="w-4 h-4" />
                  <span className="
                    text-xs uppercase
                    tracking-wider font-bold
                  ">
                    Previsão
                  </span>
                </div>

                <p className="
                  text-xl sm:text-2xl
                  font-black
                  font-['Barlow_Condensed']
                  text-white mt-2
                ">
                  {formatMoeda(
                    financeiro?.totais
                      ?.previsao_recebimento
                  )}
                </p>
              </div>


              <div className="
                rounded-xl bg-[#0A0A0A]
                border border-[#27272A]
                p-4
              ">
                <div className="
                  flex items-center gap-2
                  text-zinc-500
                ">
                  <ReceiptText className="w-4 h-4" />
                  <span className="
                    text-xs uppercase
                    tracking-wider font-bold
                  ">
                    Recebido
                  </span>
                </div>

                <p className="
                  text-xl sm:text-2xl
                  font-black
                  font-['Barlow_Condensed']
                  text-emerald-400 mt-2
                ">
                  {formatMoeda(
                    financeiro?.totais
                      ?.valor_recebido
                  )}
                </p>
              </div>


              <div className="
                rounded-xl bg-[#0A0A0A]
                border border-[#27272A]
                p-4
              ">
                <div className="
                  flex items-center gap-2
                  text-zinc-500
                ">
                  <Send className="w-4 h-4" />
                  <span className="
                    text-xs uppercase
                    tracking-wider font-bold
                  ">
                    Devido aos parceiros
                  </span>
                </div>

                <p className="
                  text-xl sm:text-2xl
                  font-black
                  font-['Barlow_Condensed']
                  text-amber-400 mt-2
                ">
                  {formatMoeda(
                    financeiro?.totais
                      ?.valor_destinar_parceiro
                  )}
                </p>
              </div>


              <div className="
                rounded-xl bg-[#0A0A0A]
                border border-[#27272A]
                p-4
              ">
                <div className="
                  flex items-center gap-2
                  text-zinc-500
                ">
                  <Clock3 className="w-4 h-4" />
                  <span className="
                    text-xs uppercase
                    tracking-wider font-bold
                  ">
                    Saldo a repassar
                  </span>
                </div>

                <p className="
                  text-xl sm:text-2xl
                  font-black
                  font-['Barlow_Condensed']
                  text-red-400 mt-2
                ">
                  {formatMoeda(
                    financeiro?.totais
                      ?.saldo_a_repassar
                  )}
                </p>
              </div>

            </div>

          )}

        </CardContent>
      </Card>


      {loading ? (

        <div className="
          grid grid-cols-1
          xl:grid-cols-2 gap-4
        ">
          {[1, 2].map(item => (
            <div
              key={item}
              className="
                h-52 rounded-xl
                bg-[#141414]
                border border-[#27272A]
                animate-pulse
              "
            />
          ))}
        </div>

      ) : parcerias.length === 0 ? (

        <Card className="
          bg-[#141414]
          border-[#27272A]
        ">

          <CardContent className="
            py-14
            flex flex-col items-center
            text-center
          ">

            <div className="
              w-14 h-14 rounded-xl
              bg-[var(--cor-primaria,#007AFF)]/10
              flex items-center justify-center
              mb-4
            ">
              <Building2 className="
                w-7 h-7
                text-[var(--cor-primaria,#007AFF)]
              " />
            </div>

            <h2 className="
              text-lg font-bold text-white
            ">
              Nenhuma parceria cadastrada
            </h2>

            <p className="
              text-sm text-zinc-500
              mt-1 max-w-md
            ">
              Cadastre escolas ou instituições parceiras
              e vincule as turmas correspondentes.
            </p>

            <Button
              onClick={abrirNova}
              className="
                mt-5
                bg-[var(--cor-primaria,#007AFF)]
                hover:opacity-90
              "
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar primeira parceria
            </Button>

          </CardContent>

        </Card>

      ) : (

        <div className="
          grid grid-cols-1
          xl:grid-cols-2 gap-4
        ">

          {parcerias.map(parceria => {

            const turmasParceria =
              Array.isArray(parceria.turmas)
                ? parceria.turmas
                : [];

            const dadosFinanceiros =
              obterFinanceiroParceria(
                parceria.id
              );

            const statusFinanceiro =
              statusRepasse(
                dadosFinanceiros
                  ?.status_repasse
              );

            return (

              <Card
                key={parceria.id}
                className="
                  bg-[#141414]
                  border-[#27272A]
                  hover:border-[#3f3f46]
                  transition-colors
                "
              >

                <CardContent className="p-5">

                  <div className="
                    flex items-start
                    justify-between gap-4
                  ">

                    <div className="
                      flex items-start gap-3
                      min-w-0
                    ">

                      <div className="
                        w-11 h-11 shrink-0
                        rounded-xl
                        bg-[var(--cor-primaria,#007AFF)]/10
                        text-[var(--cor-primaria,#007AFF)]
                        flex items-center justify-center
                      ">
                        <School className="w-5 h-5" />
                      </div>

                      <div className="min-w-0">

                        <h2 className="
                          text-lg font-bold text-white
                          truncate
                        ">
                          {parceria.nome}
                        </h2>

                        <p className="
                          text-sm text-zinc-500 truncate
                        ">
                          {parceria.email || 'Sem e-mail'}
                        </p>

                      </div>

                    </div>


                    <span className={`
                      text-xs font-bold
                      px-2.5 py-1 rounded-full
                      ${
                        parceria.ativo === false
                          ? 'bg-red-500/10 text-red-400'
                          : 'bg-emerald-500/10 text-emerald-400'
                      }
                    `}>
                      {
                        parceria.ativo === false
                          ? 'Inativa'
                          : 'Ativa'
                      }
                    </span>

                  </div>


                  <div className="
                    grid grid-cols-2 gap-3
                    mt-5
                  ">

                    <div className="
                      rounded-xl
                      bg-[#0A0A0A]
                      border border-[#27272A]
                      p-3
                    ">

                      <div className="
                        flex items-center gap-2
                        text-zinc-500
                      ">
                        <Percent className="w-4 h-4" />
                        <span className="
                          text-xs uppercase
                          tracking-wider font-bold
                        ">
                          Percentual
                        </span>
                      </div>

                      <p className="
                        text-2xl font-black
                        font-['Barlow_Condensed']
                        text-white mt-1
                      ">
                        {formatPercentual(
                          parceria.percentual
                        )}
                      </p>

                    </div>


                    <div className="
                      rounded-xl
                      bg-[#0A0A0A]
                      border border-[#27272A]
                      p-3
                    ">

                      <div className="
                        flex items-center gap-2
                        text-zinc-500
                      ">
                        <Users className="w-4 h-4" />
                        <span className="
                          text-xs uppercase
                          tracking-wider font-bold
                        ">
                          Turmas
                        </span>
                      </div>

                      <p className="
                        text-2xl font-black
                        font-['Barlow_Condensed']
                        text-white mt-1
                      ">
                        {turmasParceria.length}
                      </p>

                    </div>

                  </div>


                  <div className="
                    flex flex-wrap gap-2 mt-4
                    min-h-7
                  ">

                    {turmasParceria.length ? (

                      turmasParceria.map(turma => (

                        <span
                          key={turma.id}
                          className="
                            text-xs
                            px-2.5 py-1 rounded-lg
                            bg-zinc-800
                            text-zinc-300
                          "
                        >
                          {turma.nome}
                        </span>

                      ))

                    ) : (

                      <span className="
                        text-xs text-zinc-600
                      ">
                        Nenhuma turma vinculada
                      </span>

                    )}

                  </div>


                  <div className="
                    mt-5 rounded-xl
                    bg-[#0A0A0A]
                    border border-[#27272A]
                    overflow-hidden
                  ">

                    <div className="
                      flex items-center
                      justify-between gap-3
                      px-4 py-3
                      border-b border-[#27272A]
                    ">
                      <div className="
                        flex items-center gap-2
                      ">
                        <WalletCards className="
                          w-4 h-4
                          text-[var(--cor-primaria,#007AFF)]
                        " />

                        <span className="
                          text-xs uppercase
                          tracking-wider
                          font-bold text-zinc-400
                        ">
                          Resumo financeiro
                        </span>
                      </div>

                      {loadingFinanceiro ? (
                        <Loader2 className="
                          w-4 h-4
                          animate-spin text-zinc-500
                        " />
                      ) : (
                        <span className={`
                          text-[11px] font-bold
                          px-2.5 py-1 rounded-full
                          ${statusFinanceiro.classe}
                        `}>
                          {statusFinanceiro.label}
                        </span>
                      )}
                    </div>


                    {loadingFinanceiro ? (

                      <div className="
                        h-36 animate-pulse
                        bg-zinc-900/40
                      " />

                    ) : dadosFinanceiros ? (

                      <div className="p-4">

                        <div className="
                          grid grid-cols-2
                          gap-x-4 gap-y-4
                        ">

                          <div>
                            <p className="
                              text-xs text-zinc-600
                            ">
                              Alunos
                            </p>
                            <p className="
                              text-sm font-bold
                              text-white mt-1
                            ">
                              {
                                dadosFinanceiros
                                  .quantidade_alunos || 0
                              }
                            </p>
                          </div>


                          <div>
                            <p className="
                              text-xs text-zinc-600
                            ">
                              Previsão das turmas
                            </p>
                            <p className="
                              text-sm font-bold
                              text-white mt-1
                            ">
                              {formatMoeda(
                                dadosFinanceiros
                                  .previsao_recebimento
                              )}
                            </p>
                          </div>


                          <div>
                            <p className="
                              text-xs text-zinc-600
                            ">
                              Recebido
                            </p>
                            <p className="
                              text-sm font-bold
                              text-emerald-400 mt-1
                            ">
                              {formatMoeda(
                                dadosFinanceiros
                                  .valor_recebido
                              )}
                            </p>
                          </div>


                          <div>
                            <p className="
                              text-xs text-zinc-600
                            ">
                              Devido ao parceiro
                            </p>
                            <p className="
                              text-sm font-bold
                              text-amber-400 mt-1
                            ">
                              {formatMoeda(
                                dadosFinanceiros
                                  .valor_destinar_parceiro
                              )}
                            </p>
                          </div>


                          <div>
                            <p className="
                              text-xs text-zinc-600
                            ">
                              Em confirmação
                            </p>
                            <p className="
                              text-sm font-bold
                              text-blue-400 mt-1
                            ">
                              {formatMoeda(
                                dadosFinanceiros
                                  .valor_aguardando_confirmacao
                              )}
                            </p>
                          </div>


                          <div>
                            <p className="
                              text-xs text-zinc-600
                            ">
                              Confirmado
                            </p>
                            <p className="
                              text-sm font-bold
                              text-emerald-400 mt-1
                            ">
                              {formatMoeda(
                                dadosFinanceiros
                                  .valor_confirmado_parceiro
                              )}
                            </p>
                          </div>

                        </div>


                        <div className="
                          flex flex-col gap-3
                          sm:flex-row
                          sm:items-center
                          sm:justify-between
                          mt-4 pt-4
                          border-t border-[#27272A]
                        ">

                          <div>
                            <p className="
                              text-xs text-zinc-500
                            ">
                              Saldo a repassar
                            </p>

                            <p className="
                              text-xl font-black
                              font-['Barlow_Condensed']
                              text-white mt-0.5
                            ">
                              {formatMoeda(
                                dadosFinanceiros
                                  .saldo_a_repassar
                              )}
                            </p>
                          </div>


                          {
                            parceria.ativo !== false
                            && Number(
                              dadosFinanceiros
                                .saldo_a_repassar || 0
                            ) > 0
                            && (
                              <Button
                                type="button"
                                size="sm"
                                onClick={() =>
                                  abrirRepasse(parceria)
                                }
                                className="
                                  bg-[var(--cor-primaria,#007AFF)]
                                  hover:opacity-90
                                "
                              >
                                <Send className="
                                  w-4 h-4 mr-2
                                " />
                                Registrar repasse
                              </Button>
                            )
                          }

                        </div>

                      </div>

                    ) : (

                      <div className="
                        p-4 text-sm text-zinc-500
                      ">
                        Dados financeiros indisponíveis.
                      </div>

                    )}

                  </div>


                  <div className="
                    flex items-center gap-2
                    mt-5 pt-4
                    border-t border-[#27272A]
                  ">

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        abrirEdicao(parceria)
                      }
                      className="
                        border-[#3f3f46]
                        bg-transparent
                        text-zinc-300
                        hover:bg-zinc-800
                        hover:text-white
                      "
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>

                    {parceria.ativo !== false && (

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          desativar(parceria)
                        }
                        className="
                          text-red-400
                          hover:text-red-300
                          hover:bg-red-500/10
                        "
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Desativar
                      </Button>

                    )}

                  </div>

                </CardContent>

              </Card>

            );

          })}

        </div>

      )}


      <Dialog
        open={repasseDialogOpen}
        onOpenChange={open => {
          if (!salvandoRepasse) {
            setRepasseDialogOpen(open);

            if (!open) {
              setParceriaRepasse(null);
            }
          }
        }}
      >
        <DialogContent className="
          bg-[#141414]
          border-[#27272A]
          text-white
          sm:max-w-lg
        ">

          <DialogHeader>
            <DialogTitle className="
              font-['Barlow_Condensed']
              text-2xl
              tracking-tight
              uppercase
            ">
              Registrar repasse financeiro
            </DialogTitle>
          </DialogHeader>


          {parceriaRepasse && (

            <form
              onSubmit={registrarRepasse}
              className="space-y-5 mt-2"
            >

              <div className="
                rounded-xl
                bg-[#0A0A0A]
                border border-[#27272A]
                p-4
              ">

                <div className="
                  flex items-start
                  justify-between gap-4
                ">

                  <div className="min-w-0">

                    <p className="
                      text-xs uppercase
                      tracking-wider
                      font-bold text-zinc-500
                    ">
                      Parceria
                    </p>

                    <p className="
                      text-lg font-bold
                      text-white mt-1
                      truncate
                    ">
                      {parceriaRepasse.nome}
                    </p>

                    <p className="
                      text-xs text-zinc-500 mt-1
                    ">
                      Competência {competencia}
                    </p>

                  </div>


                  <div className="
                    w-10 h-10
                    rounded-xl
                    bg-[var(--cor-primaria,#007AFF)]/10
                    flex items-center
                    justify-center
                    shrink-0
                  ">
                    <Send className="
                      w-5 h-5
                      text-[var(--cor-primaria,#007AFF)]
                    " />
                  </div>

                </div>


                <div className="
                  grid grid-cols-2
                  gap-3 mt-4
                  pt-4
                  border-t border-[#27272A]
                ">

                  <div>
                    <p className="
                      text-xs text-zinc-500
                    ">
                      Devido ao parceiro
                    </p>

                    <p className="
                      text-lg font-black
                      font-['Barlow_Condensed']
                      text-amber-400 mt-1
                    ">
                      {formatMoeda(
                        parceriaRepasse
                          .financeiro
                          ?.valor_destinar_parceiro
                      )}
                    </p>
                  </div>


                  <div>
                    <p className="
                      text-xs text-zinc-500
                    ">
                      Saldo disponível
                    </p>

                    <p className="
                      text-lg font-black
                      font-['Barlow_Condensed']
                      text-white mt-1
                    ">
                      {formatMoeda(
                        parceriaRepasse
                          .financeiro
                          ?.saldo_a_repassar
                      )}
                    </p>
                  </div>

                </div>

              </div>


              <div className="space-y-2">

                <Label>
                  Valor do repasse
                </Label>

                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={repasseForm.valor}
                  onChange={e =>
                    setRepasseForm({
                      ...repasseForm,
                      valor: e.target.value,
                    })
                  }
                  placeholder="0,00"
                  className="
                    bg-[#0A0A0A]
                    border-[#27272A]
                  "
                />

                <p className="
                  text-xs text-zinc-500
                ">
                  Informe o valor efetivamente transferido
                  ao parceiro.
                </p>

                {(() => {
                  const previsto = Number(
                    parceriaRepasse.financeiro
                      ?.previsao_parceiro || 0
                  );

                  const jaDepositado = Number(
                    parceriaRepasse.financeiro
                      ?.valor_total_depositado || 0
                  );

                  const valorAtual = Number(
                    String(repasseForm.valor || 0)
                      .replace(',', '.')
                  ) || 0;

                  const excedente = Math.max(
                    jaDepositado + valorAtual - previsto,
                    0
                  );

                  if (excedente <= 0) {
                    return null;
                  }

                  return (
                    <div className="
                      mt-3 rounded-xl
                      border border-emerald-500/20
                      bg-emerald-500/5
                      p-3
                    ">
                      <p className="
                        text-sm font-semibold
                        text-emerald-400
                      ">
                        Excedente sobre o previsto:{' '}
                        {formatMoeda(excedente)}
                      </p>

                      <p className="
                        text-xs text-zinc-400 mt-1
                      ">
                        Previsto para o parceiro:{' '}
                        {formatMoeda(previsto)}
                      </p>

                      <label className="
                        flex items-start gap-3
                        mt-3 cursor-pointer
                      ">
                        <input
                          type="checkbox"
                          checked={
                            repasseForm
                              .gerar_saldo_positivo
                          }
                          onChange={e =>
                            setRepasseForm({
                              ...repasseForm,
                              gerar_saldo_positivo:
                                e.target.checked,
                            })
                          }
                          className="mt-1"
                        />

                        <span className="
                          text-sm text-zinc-300
                        ">
                          Levar o excedente como saldo
                          positivo para o próximo repasse
                        </span>
                      </label>
                    </div>
                  );
                })()}

              </div>


              <div className="space-y-2">

                <Label>
                  Data do depósito
                </Label>

                <Input
                  type="date"
                  value={
                    repasseForm.data_deposito
                  }
                  onChange={e =>
                    setRepasseForm({
                      ...repasseForm,
                      data_deposito:
                        e.target.value,
                    })
                  }
                  className="
                    bg-[#0A0A0A]
                    border-[#27272A]
                  "
                />

              </div>


              <div className="space-y-2">

                <Label>
                  Comprovante (opcional)
                </Label>

                <Input
                  type="file"
                  accept="
                    image/jpeg,
                    image/png,
                    image/webp,
                    application/pdf
                  "
                  onChange={e => {
                    const arquivo =
                      e.target.files?.[0] || null;

                    if (
                      arquivo
                      && arquivo.size > 5 * 1024 * 1024
                    ) {
                      toast.error(
                        'O comprovante deve ter no máximo 5 MB'
                      );

                      e.target.value = '';

                      setRepasseForm({
                        ...repasseForm,
                        comprovante: null,
                      });

                      return;
                    }

                    setRepasseForm({
                      ...repasseForm,
                      comprovante: arquivo,
                    });
                  }}
                  className="
                    bg-[#0A0A0A]
                    border-[#27272A]
                  "
                />

                <p className="
                  text-xs text-zinc-500
                ">
                  Foto ou PDF, até 5 MB.
                  O parceiro poderá visualizar antes
                  de confirmar o recebimento.
                </p>

              </div>


              <div className="space-y-2">

                <Label>
                  Observação
                </Label>

                <Input
                  value={repasseForm.observacao}
                  onChange={e =>
                    setRepasseForm({
                      ...repasseForm,
                      observacao:
                        e.target.value,
                    })
                  }
                  placeholder="Ex.: PIX referente ao repasse mensal"
                  className="
                    bg-[#0A0A0A]
                    border-[#27272A]
                  "
                />

              </div>


              <div className="
                rounded-xl
                bg-[#0A0A0A]
                border border-[#27272A]
                overflow-hidden
              ">

                <div className="
                  flex items-center
                  justify-between gap-3
                  px-4 py-3
                  border-b border-[#27272A]
                ">

                  <div className="
                    flex items-center gap-2
                  ">
                    <ReceiptText className="
                      w-4 h-4 text-zinc-400
                    " />

                    <span className="
                      text-xs uppercase
                      tracking-wider
                      font-bold text-zinc-400
                    ">
                      Histórico de repasses
                    </span>
                  </div>

                  {!loadingRepassesHistorico && (
                    <span className="
                      text-xs text-zinc-600
                    ">
                      {repassesHistorico.length}
                      {' '}
                      {
                        repassesHistorico.length === 1
                          ? 'registro'
                          : 'registros'
                      }
                    </span>
                  )}

                </div>


                {loadingRepassesHistorico ? (

                  <div className="
                    flex items-center
                    justify-center gap-2
                    py-8
                    text-sm text-zinc-500
                  ">
                    <Loader2 className="
                      w-4 h-4 animate-spin
                    " />
                    Carregando histórico...
                  </div>

                ) : repassesHistorico.length === 0 ? (

                  <div className="
                    px-4 py-6
                    text-center
                    text-sm text-zinc-600
                  ">
                    Nenhum repasse registrado
                    nesta competência.
                  </div>

                ) : (

                  <div className="
                    divide-y divide-[#27272A]
                    max-h-52 overflow-y-auto
                  ">

                    {repassesHistorico.map(repasse => {

                      const confirmado =
                        repasse.status === 'confirmado';

                      return (

                        <div
                          key={repasse.id}
                          className="px-4 py-3"
                        >

                          <div className="
                            flex items-start
                            justify-between gap-4
                          ">

                            <div>

                              <p className="
                                text-base font-bold
                                text-white
                              ">
                                {formatMoeda(
                                  repasse.valor
                                )}
                              </p>

                              <p className="
                                text-xs
                                text-zinc-500 mt-1
                              ">
                                Depósito em{' '}
                                {
                                  repasse.data_deposito
                                  || 'Data não informada'
                                }
                              </p>

                            </div>


                            <span className={`
                              text-[11px]
                              font-bold
                              px-2.5 py-1
                              rounded-full
                              shrink-0
                              ${
                                confirmado
                                  ? 'bg-emerald-500/10 text-emerald-400'
                                  : 'bg-blue-500/10 text-blue-400'
                              }
                            `}>
                              {
                                confirmado
                                  ? 'Confirmado'
                                  : 'Aguardando confirmação'
                              }
                            </span>

                          </div>


                          {repasse.observacao && (

                            <p className="
                              text-xs
                              text-zinc-500 mt-2
                              leading-relaxed
                            ">
                              {repasse.observacao}
                            </p>

                          )}


                          {
                            confirmado
                            && repasse.confirmado_em
                            && (
                              <div className="
                                flex items-center gap-1.5
                                mt-2
                                text-xs
                                text-emerald-500
                              ">
                                <CheckCircle2 className="
                                  w-3.5 h-3.5
                                " />
                                Recebimento confirmado
                                pelo parceiro
                              </div>
                            )
                          }

                        </div>

                      );

                    })}

                  </div>

                )}

              </div>


              <div className="
                rounded-xl
                border border-blue-500/20
                bg-blue-500/5
                p-3
              ">

                <div className="
                  flex items-start gap-2
                ">

                  <Clock3 className="
                    w-4 h-4
                    text-blue-400
                    mt-0.5 shrink-0
                  " />

                  <p className="
                    text-xs
                    leading-relaxed
                    text-zinc-400
                  ">
                    Depois do registro, o valor ficará
                    como aguardando confirmação. O parceiro
                    deverá confirmar que recebeu o depósito.
                  </p>

                </div>

              </div>


              <div className="
                flex justify-end gap-2
                pt-2
              ">

                <Button
                  type="button"
                  variant="ghost"
                  disabled={salvandoRepasse}
                  onClick={() => {
                    setRepasseDialogOpen(false);
                    setParceriaRepasse(null);
                  }}
                >
                  Cancelar
                </Button>


                <Button
                  type="submit"
                  disabled={salvandoRepasse}
                  className="
                    bg-[var(--cor-primaria,#007AFF)]
                    hover:opacity-90
                  "
                >

                  {salvandoRepasse ? (

                    <Loader2 className="
                      w-4 h-4
                      mr-2 animate-spin
                    " />

                  ) : (

                    <Send className="
                      w-4 h-4 mr-2
                    " />

                  )}

                  Registrar depósito

                </Button>

              </div>

            </form>

          )}

        </DialogContent>
      </Dialog>


      <Dialog
        open={dialogOpen}
        onOpenChange={open => {

          if (!salvando) {
            setDialogOpen(open);
          }

        }}
      >

        <DialogContent className="
          bg-[#141414]
          border-[#27272A]
          text-white
          sm:max-w-xl
          max-h-[90vh]
          overflow-y-auto
        ">

          <DialogHeader>

            <DialogTitle className="
              font-['Barlow_Condensed']
              text-2xl tracking-tight uppercase
            ">
              {
                editing
                  ? 'Editar parceria'
                  : 'Nova parceria'
              }
            </DialogTitle>

          </DialogHeader>


          <form
            onSubmit={salvar}
            className="space-y-5 mt-2"
          >

            <div className="space-y-2">

              <Label>Nome da parceria</Label>

              <Input
                value={form.nome}
                onChange={e =>
                  setForm({
                    ...form,
                    nome: e.target.value,
                  })
                }
                placeholder="Ex.: Escola Adventista"
                className="
                  bg-[#0A0A0A]
                  border-[#27272A]
                "
              />

            </div>


            <div className="space-y-2">

              <Label>E-mail de acesso</Label>

              <Input
                type="email"
                value={form.email}
                onChange={e =>
                  setForm({
                    ...form,
                    email: e.target.value,
                  })
                }
                placeholder="parceiro@escola.com"
                className="
                  bg-[#0A0A0A]
                  border-[#27272A]
                "
              />

            </div>


            <div className="space-y-2">

              <Label>Percentual da parceria (%)</Label>

              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.percentual}
                onChange={e =>
                  setForm({
                    ...form,
                    percentual: e.target.value,
                  })
                }
                placeholder="Ex.: 40"
                className="
                  bg-[#0A0A0A]
                  border-[#27272A]
                "
              />

              <p className="text-xs text-zinc-500">
                Informe quanto da receita das turmas
                pertence ao parceiro.
              </p>

            </div>


            <div className="space-y-2">

              <Label>
                {
                  editing
                    ? 'Nova senha (opcional)'
                    : 'Senha de acesso (opcional)'
                }
              </Label>

              <Input
                type="password"
                value={form.password}
                onChange={e =>
                  setForm({
                    ...form,
                    password: e.target.value,
                  })
                }
                placeholder={
                  editing
                    ? 'Deixe vazio para manter'
                    : 'Deixe vazio para usar a senha padrão'
                }
                className="
                  bg-[#0A0A0A]
                  border-[#27272A]
                "
              />

            </div>


            <div className="space-y-3">

              <div>

                <Label>Turmas da parceria</Label>

                <p className="
                  text-xs text-zinc-500 mt-1
                ">
                  Uma parceria pode possuir várias turmas,
                  mas uma turma não pode pertencer a duas
                  parcerias ativas.
                </p>

              </div>


              <div className="
                border border-[#27272A]
                rounded-xl overflow-hidden
              ">

                {turmas.length === 0 ? (

                  <div className="
                    p-4 text-sm text-zinc-500
                  ">
                    Nenhuma turma ativa disponível.
                  </div>

                ) : (

                  turmas.map((turma, index) => {

                    const marcada =
                      form.turma_ids.includes(turma.id);

                    return (

                      <label
                        key={turma.id}
                        className={`
                          flex items-center gap-3
                          px-4 py-3
                          cursor-pointer
                          transition-colors
                          ${
                            index
                              ? 'border-t border-[#27272A]'
                              : ''
                          }
                          ${
                            marcada
                              ? 'bg-[var(--cor-primaria,#007AFF)]/10'
                              : 'hover:bg-zinc-900'
                          }
                        `}
                      >

                        <input
                          type="checkbox"
                          checked={marcada}
                          onChange={() =>
                            alternarTurma(turma.id)
                          }
                          className="
                            w-4 h-4
                            accent-[var(--cor-primaria,#007AFF)]
                          "
                        />

                        <div className="min-w-0">

                          <p className="
                            text-sm font-medium
                            text-white
                          ">
                            {turma.nome}
                          </p>

                          {turma.dias_semana?.length > 0 && (

                            <p className="
                              text-xs text-zinc-500 mt-0.5
                            ">
                              {turma.dias_semana.join(', ')}
                            </p>

                          )}

                        </div>

                      </label>

                    );

                  })

                )}

              </div>

            </div>


            <div className="
              flex justify-end gap-2
              pt-2
            ">

              <Button
                type="button"
                variant="ghost"
                disabled={salvando}
                onClick={() =>
                  setDialogOpen(false)
                }
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                disabled={salvando}
                className="
                  bg-[var(--cor-primaria,#007AFF)]
                  hover:opacity-90
                "
              >

                {salvando ? (
                  <Loader2 className="
                    w-4 h-4 mr-2 animate-spin
                  " />
                ) : null}

                {
                  editing
                    ? 'Salvar alterações'
                    : 'Criar parceria'
                }

              </Button>

            </div>

          </form>

        </DialogContent>

      </Dialog>

    </div>

  );

}
